import React from 'react';
import { motion } from 'framer-motion';
import { FiZap, FiLock, FiRefreshCw, FiCheck, FiClock } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import logo from '../../assets/images/M_Multicolor.svg';

const FEATURES = [
  { icon: <FiZap size={16} />, label: 'AI-powered extraction' },
  { icon: <FiLock size={16} />, label: 'Secure & compliant' },
  { icon: <FiRefreshCw size={16} />, label: 'Auto-sync to AMS' },
];

interface MomentumClawLandingProps {
  onRequestAccess: () => void;
  requested?: boolean;
  requesting?: boolean;
  checking?: boolean;
}

const MomentumClawLanding: React.FC<MomentumClawLandingProps> = ({ onRequestAccess, requested, requesting, checking }) => (
  <motion.div
    className="mc-content"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.35 }}
  >
    <div className="mc-card">
      <div className="mc-card__glow" />

      {requested ? (
        /* ── Success State ── */
        <motion.div
          className="mc-success"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <motion.div
            className="mc-success__icon-wrap"
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <div className="mc-success__ring" />
            <FiCheck size={44} className="mc-success__icon" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.35 }}
          >
            <h2 className="mc-success__title">Request Submitted!</h2>
            <p className="mc-success__desc">
              Your request for MomentumClaw access has been received. Our team will review it and get back to you shortly.
            </p>
          </motion.div>

          <motion.div
            className="mc-success__steps"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.35 }}
          >
            {[
              { icon: <FiCheck size={14} />, label: 'Request received', done: true },
              { icon: <FiClock size={14} />, label: 'Under review (1\u20132 business days)', done: false },
              { icon: <RiSparklingFill size={14} />, label: 'Access granted & configured', done: false },
            ].map((step, i) => (
              <div key={i} className={`mc-success__step${step.done ? ' mc-success__step--done' : ''}`}>
                <span className="mc-success__step-icon">{step.icon}</span>
                <span className="mc-success__step-label">{step.label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      ) : (
        /* ── Request State ── */
        <div className="mc-idle">
          <div className="mc-badge">
            <span className="mc-badge__dot" />
            MOMENTUM CLAW
          </div>

          <div className="mc-body">
            <div className="mc-icon-col">
              <div className="mc-icon-ring">
                <div className="mc-icon-ring__inner">
                  <img
                    src={logo}
                    alt="MomentumClaw"
                    style={{ width: 80, height: 80, objectFit: 'contain', filter: 'drop-shadow(0 0 18px rgba(194,40,121,0.4))' }}
                  />
                </div>
              </div>
            </div>

            <div className="mc-text-col">
              <h2 className="mc-title">Request MomentumClaw</h2>
              <p className="mc-desc">
                MomentumClaw automates data extraction from carrier portals and syncs it directly into your AMS — no manual entry, no missed updates, no headaches.
              </p>

              <div className="mc-features">
                {FEATURES.map((f, i) => (
                  <span key={i} className="mc-feature-tag">
                    <span className="mc-feature-tag__icon">{f.icon}</span>
                    {f.label}
                  </span>
                ))}
              </div>

              <div className="mc-actions">
                {checking ? (
                  <div className="mc-btn mc-btn--primary" style={{ pointerEvents: 'none', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiRefreshCw size={15} className="mc-spin" />
                    Checking…
                  </div>
                ) : (
                  <motion.button
                    className="mc-btn mc-btn--primary"
                    onClick={onRequestAccess}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={!!requesting}
                  >
                    {requesting ? <FiRefreshCw size={15} className="mc-spin" /> : <RiSparklingFill size={15} />}
                    {requesting ? 'Submitting…' : 'Request Access'}
                  </motion.button>
                )}
              </div>

              <p className="mc-note">
                <FiLock size={11} />
                Your request is reviewed within 1\u20132 business days.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  </motion.div>
);

export default MomentumClawLanding;
